import { computed, Injectable, signal } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { BehaviorSubject, Observable, debounceTime, fromEvent, map, shareReplay } from 'rxjs';
import { DatePipe } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  private readonly MOBILE_BREAKPOINT = 992;
  
  // Signal to track window width
  private windowWidth = signal(this.getWindowWidth());
  
  // Computed signal for mobile state
  isMobile = computed(() => this.windowWidth() < this.MOBILE_BREAKPOINT);
  
  constructor() {
    // Listen to window resize events
    if (typeof window !== 'undefined') {
      fromEvent(window, 'resize')
        .pipe(debounceTime(150))
        .subscribe(() => {
          this.windowWidth.set(this.getWindowWidth());
        });
    }
  }
  
  private getWindowWidth(): number {
    return typeof window !== 'undefined' ? window.innerWidth : 0;
  }
  
  getIsMobile(): boolean {
    return this.isMobile();
  }
  
  private mapInstance$ = new BehaviorSubject<L.Map | null>(null);

  setMap(map: L.Map) {
    this.mapInstance$.next(map);
  }

  getMap() {
    return this.mapInstance$.value;
  }


 getLocalTimestampAtTime(date: Date, time: string): number {
  const [hh, mm] = time.split(":").map(Number);

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hh,
    mm,
    0,
    0
  ).getTime();
}



  formatDateYYYYMMDD(d: Date): string {
    const y = d.getFullYear();
    const m = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    return `${y}${m}${day}`;
  }
  private currentLegend: any = null;

private datePipe = new DatePipe('en-US');
  
  formatTimestamp(timestamp: number,format='d MMMM yyyy'): string {
    return this.datePipe.transform(timestamp, format) || '';
  }
 

  addCustomLegend(_legendControl?: any, map?: any, leaflet?: any, legendConfig?: any): void {

  // ⭐ Remove old legend if exists
  if (this.currentLegend) {
    map.removeControl(this.currentLegend);
    this.currentLegend = null;
  }

  const legendElement = this.createLegendElement(legendConfig);
  leaflet.DomEvent.disableClickPropagation(legendElement);
leaflet.DomEvent.disableScrollPropagation(legendElement);

  // Create Leaflet control
  const CustomLegendControl = leaflet.Control.extend({
    options: {
      position: 'bottomright'
    },
    onAdd: () => legendElement
  });

  // ⭐ Save newly created legend
  this.currentLegend = new CustomLegendControl();
  this.currentLegend.addTo(map);
}

    // Create legend HTML element
    private createLegendElement(config: any): HTMLElement {
      const container = document.createElement('div');
      container.className = `legend-container ${config.containerClass ?? ''}`;
      container.style.background = 'white';
      container.style.padding = '8px';
      container.style.borderRadius = '4px';
      container.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
      container.style.display = 'flex';
      container.style.alignItems = 'center';
      container.id = 'map-legend';
  
      // Check if this is a multi-section legend (cyclone) or single-section (rainfall, wind, etc.)
      if (config.sections && Array.isArray(config.sections)) {
        // Multi-section legend (for cyclone) - each section is a separate box
        const sectionsContainer = document.createElement('div');
        sectionsContainer.style.display = 'flex';
        sectionsContainer.style.flexDirection = 'column';
        sectionsContainer.style.gap = '8px';

        config.sections.forEach((section: any) => {
          // Create a completely separate box for each section
          const sectionBox = document.createElement('div');
          sectionBox.style.background = 'white';
          sectionBox.style.padding = '6px';
          sectionBox.style.borderRadius = '4px';
          sectionBox.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
          sectionBox.style.display = 'flex';
          sectionBox.style.alignItems = 'center';
          sectionBox.style.gap = '6px';

          // Section title
          if (section.showTitle !== false && section.title) {
            const titleEl = document.createElement('span');
            titleEl.className = 'section-title';
            titleEl.textContent = section.title;
            titleEl.style.writingMode = 'vertical-rl';
            titleEl.style.transform = 'rotate(180deg)';
            titleEl.style.display = 'flex';
            titleEl.style.alignItems = 'center';
            titleEl.style.justifyContent = 'center';
            titleEl.style.borderRight = '1px solid #e5e7eb';
            titleEl.style.paddingLeft = '4px';
            titleEl.style.paddingRight = '4px';
            titleEl.style.fontWeight = 'bold';
            titleEl.style.fontSize = '9px';
            titleEl.style.minWidth = '16px';
            sectionBox.appendChild(titleEl);
          }

          // Section items
          const itemsDiv = document.createElement('div');
          itemsDiv.style.display = 'flex';
          itemsDiv.style.flexDirection = 'row';
          itemsDiv.style.gap = '2px';
          itemsDiv.style.flexWrap = 'nowrap';
          itemsDiv.style.alignItems = 'center';

          for (const item of section.items) {
            const span = this.createLegendItem(item);
            span.style.minWidth = '28px';
            span.style.fontSize = '9px';
            span.style.padding = '3px 6px';
            itemsDiv.appendChild(span);
          }

          sectionBox.appendChild(itemsDiv);
          sectionsContainer.appendChild(sectionBox);
        });

        container.style.background = 'transparent';
        container.style.boxShadow = 'none';
        container.style.padding = '0';
        container.appendChild(sectionsContainer);
      } else {
        // Single-section legend (original format for rainfall, wind, etc.)
        if (config.showTitle !== false) {
          const titleEl = document.createElement('span');
          titleEl.className = 'unit';
          titleEl.textContent = config.title ?? '';
      
          // make vertical
          titleEl.style.writingMode = 'vertical-rl';
          titleEl.style.transform = 'rotate(180deg)';
          titleEl.style.display = 'flex';
          titleEl.style.alignItems = 'center';
          titleEl.style.justifyContent = 'center';
      
          titleEl.style.borderRight = '1px solid #e5e7eb';
          titleEl.style.paddingLeft = '8px';
          titleEl.style.paddingRight = '8px';
          titleEl.style.fontWeight = 'bold';
          titleEl.style.minWidth = '20px';
      
          container.appendChild(titleEl);
        }
    
        const rangeDiv = document.createElement('div');
        rangeDiv.className = 'range';
        rangeDiv.style.display = 'flex';
        rangeDiv.style.flexDirection = 'column';
        rangeDiv.style.gap = '4px';
        rangeDiv.style.paddingLeft = '8px';
    
        for (const item of config.items) {
          const span = this.createLegendItem(item);
          span.style.minWidth = '80px';
          span.style.width = 'auto';
          span.style.whiteSpace = 'nowrap';
          span.style.minHeight = '22px';
          rangeDiv.appendChild(span);
        }
    
        container.appendChild(rangeDiv);
      }

      return container;
    }

    // Helper method to create individual legend items
    private createLegendItem(item: any): HTMLElement {
      const span = document.createElement('span');
      span.className = 'text-center legend-item';
      span.textContent = item.label;
      
      // Add tooltip
      if (item.tooltip) {
        span.setAttribute('title', item.tooltip);
      } else {
        span.setAttribute('title', item.label);
      }
      
      span.style.background = item.color;
      if (item.textColor) span.style.color = item.textColor;
      if (this.isLightColor(item.color)) {
        span.style.border = '1px solid #9ca3af';
      } else {
        span.style.border = '1px solid transparent';
      }

      span.style.boxSizing = 'border-box';
      span.style.padding = '4px 8px';
      span.style.display = 'inline-block';
      span.style.textAlign = 'center';
      span.style.fontSize = '11px';
      span.style.fontWeight = '500';
      span.style.cursor = 'default';
      span.style.transition = 'transform 0.2s ease';
      span.style.marginBottom = '2px';
      
      // Add hover effect
      span.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
      span.addEventListener('mouseenter', () => {
        span.style.transform = 'scale(1.1)';
        span.style.zIndex = '10';
      });
      
      span.addEventListener('mouseleave', () => {
        span.style.transform = 'scale(1)';
        span.style.zIndex = '1';
      });
      
      return span;
    }

    private isLightColor(hex: string): boolean {
  hex = hex.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // perceptual brightness
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 215;
}

private getReadableTextColor(bgColor: string): string {
  return this.isLightColor(bgColor) ? '#111827' : '#ffffff';
}
  
  // optional observable if you want to subscribe to map creation
  mapChanges$ = this.mapInstance$.asObservable();

}
